import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AppThunk } from '../store'
import { HTTP } from '../lib/axios'
import { LoadingPayload, VoicemailsLoadingState } from '../types/common'
import { sortListByDate } from '../utils/common'
import { Voicemail } from '../types/voicemail'

export interface VoicemailsState {
  loading: VoicemailsLoadingState
  voicemails: Array<Voicemail>
  unread: number
  token?: string
}

export const initialState: VoicemailsState = {
  loading: {
    voicemails: false,
    updating: false
  },
  voicemails: [],
  unread: 0,
  token: ''
}

const voicemailsSlice = createSlice({
  name: 'voicemails',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<LoadingPayload>) => {
      const clone = { ...state.loading, [payload.type]: payload.payload }
      state.loading = clone
    },
    setVoicemails: (
      state,
      {
        payload
      }: PayloadAction<{ voicemails: Array<Voicemail>; token?: string }>
    ) => {
      state.voicemails = payload.voicemails.filter((vm) => vm.duration !== 0)
      state.token = payload.token
    },
    updateKazooVoicemail: (state, { payload }: PayloadAction<any>) => {
      const clone = [...state.voicemails]
      const msgId = payload.media_id ? payload.media_id : payload
      const existing = clone.findIndex((vm) => vm.voicemailId === msgId)

      const isExist = existing > -1
      if (isExist && !payload.folder) {
        clone.splice(existing, 1)
      } else if (isExist) {
        const newStatus = payload.folder === 'saved' ? 'read' : 'completed'
        clone[existing] = {
          ...clone[existing],
          voicemailStatus: newStatus
        }
      }
      state.voicemails = clone
    },
    updateVoicemail: (state, { payload }: PayloadAction<Voicemail>) => {
      const clone = [...state.voicemails]
      const existing = clone.findIndex(
        (vm) => vm.voicemailId === payload.voicemailId
      )
      if (existing > -1) {
        clone[existing] = payload
      }
      state.voicemails = clone
    },
    setUnreadCount: (state, { payload }: PayloadAction<number>) => {
      state.unread = +payload
    }
  }
})

export const {
  setLoading,
  setVoicemails,
  updateKazooVoicemail,
  updateVoicemail,
  setUnreadCount
} = voicemailsSlice.actions
export default voicemailsSlice.reducer
export const voicemailsSelector = (state: {
  voicemailsStore: VoicemailsState
}) => state.voicemailsStore

export const getKazooVoicemails = (
  token: string,
  kazooAccountId: string,
  kazooOwnerId: string,
  kazooToken: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'voicemails', payload: true }))
    const url = `/kazoo/${kazooAccountId}/voicemails/${kazooOwnerId}`
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    const data = {
      token: kazooToken
    }
    try {
      const res = await HTTP.post<{ voicemails: any[]; token?: string }>(
        url,
        data,
        config
      )
      dispatch(
        setVoicemails({
          voicemails: res.data.voicemails,
          token: res.data.token
        })
      )
      dispatch(setLoading({ type: 'voicemails', payload: false }))
      const unread = res.data.voicemails.reduce((acc, cv) => {
        return acc + (cv.voicemailStatus !== 'read' && cv.duration ? 1 : 0)
      }, 0)
      dispatch(setUnreadCount(unread))
    } catch (err) {
      console.log(err)
      dispatch(setLoading({ type: 'voicemails', payload: false }))
    }
  }
}
export const getVoicemails = (
  token: string,
  accountId: string,
  australisId: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'voicemails', payload: true }))
    try {
      const res = await HTTP.get(
        `/voip/${accountId}/user/${australisId}/voicemail`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      dispatch(setVoicemails({ voicemails: res.data.voicemails }))
      dispatch(setLoading({ type: 'voicemails', payload: false }))
    } catch (err) {
      console.log(err)
      dispatch(setLoading({ type: 'voicemails', payload: false }))
    }
  }
}

export const deleteKazooVoicemail = (
  token: string,
  kazooAccountId: string,
  voicemailBoxId: string,
  payload: any
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'updating', payload: true }))
    try {
      const url = `/kazoo/${kazooAccountId}/vmboxes/${voicemailBoxId}/messages`
      console.log(payload)
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: JSON.stringify(payload)
      }

      const res = await HTTP.delete(url, config)
      const { succeeded, failed } = res.data

      // TODO: Handle failed delete
      dispatch(updateKazooVoicemail(succeeded[0]))
      dispatch(setLoading({ type: 'updating', payload: false }))
    } catch (err) {
      console.log(err)
      dispatch(setLoading({ type: 'updating', payload: false }))
    }
  }
}

export const postKazooVoicemail = (
  token: string,
  kazooAccountId: string,
  voicemailBoxId: string,
  voicemailId: string,
  payload: any
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'updating', payload: true }))
    try {
      const baseUrl = 'https://api.cloudpbx.ca:8443/v2/accounts'
      const kazooMediaUrl = `${baseUrl}/${kazooAccountId}/vmboxes/${voicemailBoxId}/messages/${voicemailId}`
      const config = {
        headers: {
          'X-Auth-Token': token
        }
      }
      const res = await HTTP.post(kazooMediaUrl, payload, config)

      dispatch(updateKazooVoicemail(res.data.data))
      dispatch(setLoading({ type: 'updating', payload: false }))
    } catch (err) {
      console.log(err)
      dispatch(setLoading({ type: 'updating', payload: false }))
    }
  }
}

export const postVoicemail = (
  token: string,
  accountId: string,
  australisId: string,
  voicemailId: string,
  payload: any
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'updating', payload: true }))
    try {
      const res = await HTTP.post(
        `/voip/${accountId}/user/${australisId}/voicemail/${voicemailId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      dispatch(updateVoicemail(res.data.voicemail))
      dispatch(setLoading({ type: 'updating', payload: false }))
    } catch (err) {
      console.log(err)
      dispatch(setLoading({ type: 'updating', payload: false }))
    }
  }
}

export const registerUnreadCountPoll = (
  token: string,
  kazooAccountId: string,
  kazooOwnerId: string,
  kazooToken: string
): AppThunk => {
  return async (dispatch) => {
    const url = `/kazoo/${kazooAccountId}/voicemails/${kazooOwnerId}`
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    const data = {
      token: kazooToken
    }
    try {
      const res = await HTTP.post<{ voicemails: any[] }>(url, data, config)

      const unread = res.data.voicemails.reduce(
        (acc, cv) => acc + (cv.voicemailStatus !== 'read' ? 1 : 0),
        0
      )
      dispatch(setUnreadCount(unread))
    } catch (err) {
      console.log(err)
    }
  }
}
